import { Link } from 'react-router-dom';
import type { TimedSettlementFinancialTotals } from '@pokerroom/types';
import { useTranslation } from 'react-i18next';

/** Presentation pieces of the timed settlement page: the step guide and the grouped totals. */

export type SettlementStepNo = 1 | 2 | 3 | 4;

export type SettlementBlocker = 'saveFailed' | 'awardsExceedPool';

/** Four steps: members, count, prepare/print, final CP. Confirmation never implies payment. */
export function settlementStep(state: {
  hasDraft: boolean;
  dirty: boolean;
  confirmed: boolean;
  hasPreview: boolean;
  previewStale: boolean;
  missingCounts: number;
  chipDifference: number;
  denominationBlocked: boolean;
  counting: boolean;
  saveFailed: boolean;
  awardsExceedPool: boolean;
}): { step: SettlementStepNo; blocker: SettlementBlocker | null } {
  if (state.saveFailed) return { step: 1, blocker: 'saveFailed' };
  if (state.dirty || (!state.hasDraft && !state.confirmed)) return { step: 1, blocker: null };
  if (state.confirmed) return { step: 4, blocker: null };
  if (
    !state.counting ||
    !state.hasPreview ||
    state.previewStale ||
    state.missingCounts > 0 ||
    state.chipDifference !== 0 ||
    state.denominationBlocked
  )
    return { step: 2, blocker: null };
  if (state.awardsExceedPool) return { step: 3, blocker: 'awardsExceedPool' };
  return { step: 3, blocker: null };
}

const STEP_TARGETS: Record<SettlementStepNo, string> = {
  1: '#settlement-data',
  2: '/tables',
  3: '#settlement-preview',
  4: '#settlement-finalize',
};

const ACTION_BASE =
  'inline-flex min-h-11 items-center justify-center px-4 text-sm font-medium transition-colors';
const PRIMARY_ACTION = `${ACTION_BASE} bg-primary text-primary-content hover:bg-primary/90`;
const OUTLINE_ACTION = `${ACTION_BASE} border border-base-300 text-base-content hover:bg-base-300`;

/** `emphasis` is false when the current step's own button already is the page's primary action. */
export function SettlementSteps({
  step,
  blocker = null,
  emphasis = true,
  payoutAvailable = false,
}: {
  step: SettlementStepNo;
  blocker?: SettlementBlocker | null;
  emphasis?: boolean;
  payoutAvailable?: boolean;
}) {
  const { t } = useTranslation('tournament');
  const steps: SettlementStepNo[] = [1, 2, 3, 4];
  // A blocker is fixed in the settlement data (rules, members or a failed save).
  const target = blocker ? STEP_TARGETS[1] : STEP_TARGETS[step];
  const ACTION = emphasis ? PRIMARY_ACTION : OUTLINE_ACTION;
  const action = blocker
    ? t('settlementFlow.fixData')
    : step === 2
      ? t('settlementFlow.goToTables')
      : t(`settlementFlow.action${step}`);
  return (
    <section className="border-2 border-primary bg-base-200 print:hidden">
      <ol
        aria-label={t('settlementFlow.label')}
        className="grid grid-cols-2 sm:grid-cols-4 border-b border-base-300"
      >
        {steps.map((n) => (
          <li
            key={n}
            aria-current={n === step ? 'step' : undefined}
            className={`relative flex min-h-11 items-center gap-2 px-3 py-2 text-sm ${n === step ? 'bg-primary text-primary-content font-bold' : n < step ? 'text-base-content' : 'text-base-content/70'}`}
          >
            <span className="tabular-nums">{n}</span>
            <span>{t(`settlementFlow.step${n}`)}</span>
            {n < step && <span className="sr-only">{t('settlementFlow.done')}</span>}
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <p className={`text-base ${blocker ? 'font-semibold text-error' : 'text-base-content'}`}>
          {blocker ? t(`settlementFlow.blocked.${blocker}`) : t(`settlementFlow.hint${step}`)}
        </p>
        {target.startsWith('#') ? (
          <a href={target} className={ACTION}>
            {action}
          </a>
        ) : (
          <Link to={target} className={ACTION}>
            {action}
          </Link>
        )}
      </div>
    </section>
  );
}

export interface SettlementTotals {
  participants: number;
  raCount: number;
  units: number;
  expectedChips: number;
  countedChips: number;
  chipDifference: number;
  prizePoolPrincipal: number;
  activityFee1Total: number;
  netPool: number;
  rankBudget: number;
  rankResidual: number;
  totalAwards: number;
  activityFee2: number;
}

type TotalKey = keyof SettlementTotals;

const GROUPS: Array<{ id: 'entries' | 'chips' | 'money'; keys: TotalKey[] }> = [
  { id: 'entries', keys: ['participants', 'raCount', 'units'] },
  { id: 'chips', keys: ['expectedChips', 'countedChips', 'chipDifference'] },
  {
    id: 'money',
    keys: [
      'prizePoolPrincipal',
      'activityFee1Total',
      'netPool',
      'rankBudget',
      'totalAwards',
      'rankResidual',
      'activityFee2',
    ],
  },
];

// Same formatting as the page's other settlement figures.
const figure = (value: number) => value.toLocaleString('zh-TW');

export function SettlementSummary({
  totals,
  keys,
}: {
  totals: SettlementTotals;
  keys?: TotalKey[];
}) {
  const { t } = useTranslation('tournament');
  const groups = GROUPS.map((group) => ({
    ...group,
    keys: keys ? group.keys.filter((key) => keys.includes(key)) : group.keys,
  })).filter((group) => group.keys.length > 0);
  return (
    <div
      className={`grid gap-3 ${groups.length === 3 ? 'lg:grid-cols-[1fr_1fr_2fr]' : 'lg:grid-cols-[1fr_2fr]'}`}
    >
      {groups.map((group) => (
        <section key={group.id} className="border border-base-300 bg-base-100 p-3">
          <h3 className="mb-2 text-sm font-semibold text-base-content">
            {t(`settlementSummary.${group.id}`)}
          </h3>
          <dl
            className={`grid gap-x-4 gap-y-3 ${group.id === 'money' ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-1'}`}
          >
            {group.keys.map((key) => {
              const value = totals[key];
              const tone =
                key === 'chipDifference' && value !== 0 ? 'text-error' : 'text-base-content';
              const size =
                group.id === 'money'
                  ? 'text-xl font-bold underline underline-offset-4 decoration-2'
                  : 'text-lg font-semibold';
              return (
                <div key={key} className="min-w-0">
                  <dt className="text-xs text-base-content/70">{t(`settlementSummary.${key}`)}</dt>
                  <dd className={`${size} ${tone} tabular-nums`}>{figure(value)}</dd>
                </div>
              );
            })}
          </dl>
        </section>
      ))}
    </div>
  );
}

export interface CpCheckParticipant {
  memberNo: string;
  displayName?: string;
  nickname?: string;
  seat: string | null;
  tableNo?: number | null;
  seatNo?: number | null;
  settlementMethod?: 'CASH' | 'ZIPA';
  collectedVenueFee?: string;
}

/**
 * What CP compares before confirming: the roster, who has a seat, and the venue fee
 * staff entered per payment method. A blank fee is counted, not read as zero.
 */
export function cpCheckSummary(participants: CpCheckParticipant[]) {
  const fee = { CASH: 0, ZIPA: 0 };
  // Legacy drafts carry no fee or method fields; there is nothing to compare.
  const withFees = participants.some(
    (p) => p.settlementMethod !== undefined || p.collectedVenueFee !== undefined,
  );
  let feeMissing = 0;
  const unseated: string[] = [];
  for (const p of participants) {
    const seated = p.seat !== null || (p.tableNo != null && p.seatNo != null);
    if (!seated) unseated.push(p.displayName ?? p.nickname ?? p.memberNo);
    if (!withFees) continue;
    const amount = p.collectedVenueFee?.trim() ? Number(p.collectedVenueFee) : NaN;
    if (!p.settlementMethod || !Number.isFinite(amount)) feeMissing++;
    else fee[p.settlementMethod] += amount;
  }
  return {
    people: participants.length,
    seated: participants.length - unseated.length,
    unseated,
    withFees,
    fee,
    feeMissing,
  };
}

export function CpCheckPanel({
  summary,
  raCount,
  totals,
  checked,
  disabled,
  onChange,
}: {
  summary: ReturnType<typeof cpCheckSummary>;
  raCount: number;
  totals: TimedSettlementFinancialTotals;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { t } = useTranslation('tournament');
  // Display-only reconciliation from the exact prepared sheet; missing legacy fees stay unknown.
  const venueFee =
    summary.withFees && summary.feeMissing === 0 ? summary.fee.CASH + summary.fee.ZIPA : null;
  const fee1 = Number.isFinite(totals.activityFee1Total) ? totals.activityFee1Total : null;
  const fee2 = Number.isFinite(totals.activityFee2) ? totals.activityFee2 : null;
  const combined =
    venueFee !== null && fee1 !== null && fee2 !== null ? venueFee + fee1 + fee2 : null;
  const amounts = [
    ['venueFee', venueFee],
    ['activityFee1', fee1],
    ['activityFee2', fee2],
    ['combinedFees', combined],
  ] as const;
  return (
    <div className="space-y-2 border border-base-300 p-3 print:hidden">
      <p className="font-semibold">{t('settlementPage.cpCheck.title')}</p>
      <ul className="space-y-1 text-sm">
        <li>
          {t('settlementPage.cpCheck.roster', {
            people: summary.people,
            seated: summary.seated,
            ra: raCount,
          })}
        </li>
        {summary.unseated.length > 0 && (
          <li className="text-warning">
            {t('settlementPage.cpCheck.unseated', { names: summary.unseated.join('、') })}
          </li>
        )}
        {summary.withFees && (
          <li>
            {t('settlementPage.cpCheck.fees', {
              cash: summary.fee.CASH.toLocaleString(),
              zipa: summary.fee.ZIPA.toLocaleString(),
            })}
          </li>
        )}
        {summary.feeMissing > 0 && (
          <li className="text-warning">
            {t('settlementPage.cpCheck.feeMissing', { count: summary.feeMissing })}
          </li>
        )}
      </ul>
      <dl
        aria-label={t('settlementPage.cpCheck.amounts')}
        className="grid grid-cols-2 gap-3 py-2 sm:grid-cols-4"
      >
        {amounts.map(([key, amount]) => (
          <div key={key} className="border border-base-300 p-3 text-center">
            <dt className="text-sm">{t(`settlementPage.cpCheck.${key}`)}</dt>
            <dd className="mt-1 text-xl font-bold tabular-nums underline underline-offset-4">
              {amount === null
                ? '—'
                : amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </dd>
          </div>
        ))}
      </dl>
      <label className="flex min-h-11 items-center gap-2 text-sm font-semibold">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        {t('settlementPage.cpCheck.confirm')}
      </label>
    </div>
  );
}
